import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { Cookie, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TEXTS = {
  fr: {
    title: "Nous utilisons des cookies",
    desc: "Ce site utilise des cookies pour améliorer votre expérience et afficher des publicités personnalisées via Google AdSense.",
    accept: "Accepter",
    refuse: "Refuser",
    more: "En savoir plus",
  },
  en: {
    title: "We use cookies",
    desc: "This site uses cookies to improve your experience and display personalized ads via Google AdSense.",
    accept: "Accept",
    refuse: "Decline",
    more: "Learn more",
  },
  es: {
    title: "Usamos cookies",
    desc: "Este sitio utiliza cookies para mejorar su experiencia y mostrar anuncios personalizados a través de Google AdSense.",
    accept: "Aceptar",
    refuse: "Rechazar",
    more: "Más información",
  },
  ar: {
    title: "نستخدم ملفات تعريف الارتباط",
    desc: "يستخدم هذا الموقع ملفات تعريف الارتباط لتحسين تجربتك وعرض إعلانات مخصصة عبر Google AdSense.",
    accept: "قبول",
    refuse: "رفض",
    more: "اعرف المزيد",
  },
};

export default function CookieBanner() {
  const { theme } = useTheme();
  const { lang } = useLang();
  const navigate = useNavigate();
  const isDark = theme === "dark";
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("dn_cookie_consent");
    if (!consent) setVisible(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem("dn_cookie_consent", "accepted");
    setVisible(false);
  };

  const handleRefuse = () => {
    localStorage.setItem("dn_cookie_consent", "refused");
    setVisible(false);
  };

  if (!visible) return null;

  const t = TEXTS[lang] || TEXTS.fr;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[90] p-4 animate-fadeInUp"
    >
      <div
        className="max-w-md mx-auto rounded-2xl p-5 backdrop-blur-xl"
        style={{
          background: isDark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)",
          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 -4px 32px rgba(0,0,0,0.3)",
        }}
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${isDark ? "bg-amber-500/20" : "bg-amber-500/10"}`}>
            <Cookie size={16} className="text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {t.title}
            </p>
            <p className={`text-xs mt-1 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {t.desc}{" "}
              <button
                onClick={() => { handleAccept(); navigate("/privacy"); }}
                className={`underline underline-offset-2 ${isDark ? "text-amber-400" : "text-amber-600"}`}
              >
                {t.more}
              </button>
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                data-testid="cookie-accept"
                onClick={handleAccept}
                className="btn-gold py-2 px-5 text-xs font-semibold"
              >
                {t.accept}
              </button>
              <button
                data-testid="cookie-refuse"
                onClick={handleRefuse}
                className={`py-2 px-5 rounded-full text-xs font-semibold transition-all ${
                  isDark
                    ? "bg-white/5 text-slate-400 hover:bg-white/10"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {t.refuse}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
